/**
 * @fileoverview Doctor Profile Form Component
 * @description Form for doctor profile management with read-only email
 * @author MCHD Room Scheduler Team
 * @created 07-10-2025
 * @complies @hospital-domain.mdc - No password reset, read-only email
 */

import { useEffect } from 'react';
import {
  Card,
  TextInput,
  Stack,
  Group,
  Button,
  Text,
  Divider,
  Badge,
  Alert,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconUser,
  IconMail,
  IconStethoscope,
  IconBuildingHospital,
  IconAlertCircle,
  IconCheck,
  IconLock,
} from '@tabler/icons-react';
import {
  useDoctorProfile,
  useUpdateDoctorProfile,
  extractProfile,
  getProfileError,
} from '../../../hooks/doctor/useDoctorProfile';
import type { DoctorProfileUpdateData } from '../../../types/doctor';

/**
 * Props for DoctorProfileForm component
 */
export interface DoctorProfileFormProps {
  /** Optional custom class name */
  className?: string;
}

/**
 * Doctor Profile Form Component
 * @description Editable profile form with read-only email field
 * 
 * @example
 * ```tsx
 * function DoctorProfilePage() {
 *   return (
 *     <Container>
 *       <Title>My Profile</Title>
 *       <DoctorProfileForm />
 *     </Container>
 *   );
 * }
 * ```
 */
export function DoctorProfileForm({ className }: DoctorProfileFormProps) {
  // Fetch profile data
  const profileQuery = useDoctorProfile();
  const profile = extractProfile(profileQuery);
  const profileError = getProfileError(profileQuery);

  // Update mutation
  const updateMutation = useUpdateDoctorProfile();

  // Form state with Mantine useForm
  const form = useForm<DoctorProfileUpdateData>({
    initialValues: {
      name: '',
      specialty: '',
      department: '',
    },
    validate: {
      name: (value) => {
        if (!value || value.trim().length === 0) {
          return 'Name is required';
        }
        if (value.length < 2) {
          return 'Name must be at least 2 characters';
        }
        if (value.length > 100) {
          return 'Name must not exceed 100 characters';
        }
        return null;
      },
      specialty: (value) => {
        if (value && value.length > 100) {
          return 'Specialty must not exceed 100 characters';
        }
        return null;
      },
      department: (value) => {
        if (value && value.length > 100) {
          return 'Department must not exceed 100 characters';
        }
        return null;
      },
    },
  });

  // Populate form when profile loads
  useEffect(() => {
    if (profile) {
      form.setValues({
        name: profile.name || '',
        specialty: profile.specialty || '',
        department: profile.department || '',
      });
    }
  }, [profile]);

  /**
   * Handle form submission
   */
  const handleSubmit = (values: DoctorProfileUpdateData) => {
    updateMutation.mutate(values, {
      onSuccess: (response) => {
        if (response.success) {
          notifications.show({
            title: 'Profile Updated',
            message: 'Your profile has been updated successfully.',
            color: 'green',
            icon: <IconCheck size={16} />,
          });
        } else {
          notifications.show({
            title: 'Update Failed',
            message: response.error?.message || 'Failed to update profile',
            color: 'red',
            icon: <IconAlertCircle size={16} />,
          });
        }
      },
      onError: (error) => {
        notifications.show({
          title: 'Update Failed',
          message: error.message || 'An unexpected error occurred',
          color: 'red',
          icon: <IconAlertCircle size={16} />,
        });
      },
    });
  };

  /**
   * Reset form to original values
   */
  const handleReset = () => {
    if (profile) {
      form.setValues({
        name: profile.name || '',
        specialty: profile.specialty || '',
        department: profile.department || '',
      });
    }
  };

  if (profileQuery.isLoading) {
    return <ProfileFormSkeleton />;
  }

  if (profileError) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        {profileError}
      </Alert>
    );
  }

  if (!profile) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="No Profile Data" color="yellow">
        Unable to load profile data. Please refresh the page.
      </Alert>
    );
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder {...(className && { className })}>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="lg">
          {/* Header */}
          <div>
            <Text fw={600} size="lg">
              Profile Information
            </Text>
            <Text size="sm" c="dimmed">
              Manage your personal information
            </Text>
          </div>

          <Divider />

          {/* Email (Read-Only) */}
          <Alert
            icon={<IconLock size={16} />}
            title="Email Address (Read-Only)"
            color="blue"
            variant="light"
          >
            <Group gap="xs">
              <IconMail size={16} aria-hidden="true" />
              <Text size="sm" fw={500}>
                {profile.email}
              </Text>
            </Group>
            <Text size="xs" c="dimmed" mt="xs">
              Per hospital security policy, email addresses cannot be changed. Contact your administrator if you need assistance.
            </Text>
          </Alert>

          {/* Name (Editable) */}
          <TextInput
            label="Full Name"
            placeholder="Enter your full name"
            leftSection={<IconUser size={16} />}
            required
            {...form.getInputProps('name')}
            aria-label="Full name"
          />

          {/* Specialty (Editable) */}
          <TextInput
            label="Medical Specialty"
            placeholder="e.g., Cardiology, Neurology"
            leftSection={<IconStethoscope size={16} />}
            {...form.getInputProps('specialty')}
            aria-label="Medical specialty"
          />

          {/* Department (Editable) */}
          <TextInput
            label="Department"
            placeholder="e.g., Internal Medicine, Surgery"
            leftSection={<IconBuildingHospital size={16} />}
            {...form.getInputProps('department')}
            aria-label="Department"
          />

          {/* Account Status */}
          <Group gap="xs">
            <Text size="sm" c="dimmed">
              Account Status:
            </Text>
            <Badge color={profile.isActive ? 'green' : 'red'} variant="light">
              {profile.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </Group>

          {/* Last Login */}
          {profile.lastLogin && (
            <Text size="xs" c="dimmed">
              Last login: {new Date(profile.lastLogin).toLocaleString('en-US', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </Text>
          )}

          <Divider />

          {/* Action Buttons */}
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              color="gray"
              onClick={handleReset}
              disabled={updateMutation.isPending || !form.isDirty()}
            >
              Reset
            </Button>
            <Button
              type="submit"
              loading={updateMutation.isPending}
              disabled={!form.isDirty() || !form.isValid()}
              leftSection={updateMutation.isPending ? undefined : <IconCheck size={16} />}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </Group>

          {/* No Password Reset Notice */}
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Password Management"
            color="yellow"
            variant="light"
          >
            <Text size="xs">
              Per hospital security policy, password changes must be requested through your system administrator.
              Self-service password reset is not available for security reasons.
            </Text>
          </Alert>
        </Stack>
      </form>
    </Card>
  );
}

/**
 * Skeleton loader for profile form
 */
function ProfileFormSkeleton() {
  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="lg">
        <div style={{ height: 24, width: 200, backgroundColor: '#e9ecef', borderRadius: 4 }} />
        <div style={{ height: 40, width: '100%', backgroundColor: '#e9ecef', borderRadius: 4 }} />
        <div style={{ height: 40, width: '100%', backgroundColor: '#e9ecef', borderRadius: 4 }} />
        <div style={{ height: 40, width: '100%', backgroundColor: '#e9ecef', borderRadius: 4 }} />
        <div style={{ height: 40, width: '100%', backgroundColor: '#e9ecef', borderRadius: 4 }} />
      </Stack>
    </Card>
  );
}

